import type { Mood, Scale } from "@andrum/schemas";
import type { CheckInRow } from "@andrum/database";
import { getSupabase } from "@/lib/supabase/client";
import { enqueue } from "@/lib/offline/queue";
import { t } from "@/i18n";

export type CreateCheckInInput = {
  mood: Mood;
  energy: Scale | null;
  stress: Scale | null;
  note?: string;
  clientCreatedAt: string;
  idempotencyKey: string;
  /** Optional: skip getSession when caller already has user id */
  userId?: string;
};

/**
 * Persist check-in. Uses local session (no network round-trip for auth)
 * so the path is one Supabase upsert.
 */
export async function createCheckIn(
  input: CreateCheckInInput
): Promise<
  { ok: true; data: CheckInRow } | { ok: false; queued: boolean; message: string }
> {
  const supabase = getSupabase();
  if (!supabase) {
    enqueue({ type: "CREATE_CHECK_IN", payload: input });
    return { ok: false, queued: true, message: t("errors.savedLocalDemo") };
  }

  let userId = input.userId;
  if (!userId) {
    // getSession is local/cached; getUser() would hit the network and feel slow
    const {
      data: { session },
    } = await supabase.auth.getSession();
    userId = session?.user?.id;
  }

  if (!userId) {
    enqueue({ type: "CREATE_CHECK_IN", payload: input });
    return { ok: false, queued: true, message: t("errors.savedLocalOffline") };
  }

  const { data, error } = await supabase
    .from("check_ins")
    .upsert(
      {
        user_id: userId,
        mood: input.mood,
        energy: input.energy,
        stress: input.stress,
        note_ciphertext: input.note ?? null,
        client_created_at: input.clientCreatedAt,
        idempotency_key: input.idempotencyKey,
      },
      { onConflict: "idempotency_key" }
    )
    .select("*")
    .single();

  if (error || !data) {
    enqueue({ type: "CREATE_CHECK_IN", payload: input });
    return {
      ok: false,
      queued: true,
      message: t("errors.syncQueued"),
    };
  }

  return { ok: true, data };
}

export async function listRecentCheckIns(limit = 14): Promise<CheckInRow[]> {
  const supabase = getSupabase();
  if (!supabase) return [];

  const {
    data: { session },
  } = await supabase.auth.getSession();
  const userId = session?.user?.id;
  if (!userId) return [];

  const { data } = await supabase
    .from("check_ins")
    .select("*")
    .eq("user_id", userId)
    .order("client_created_at", { ascending: false })
    .limit(limit);

  return data ?? [];
}
